<template>
  <debug-code
    :value-code="field"
    :show-debug-code="field.watchType === 'code'"
  >
    <a-input
      :value="_value"
      readonly
      size="small"
      style="background-color: #f5f5f5"
    ></a-input>
  </debug-code>
  <span
    v-if="errorMsg"
    class="error-msg"
  >
    {{ errorMsg }}
  </span>
</template>

<script setup lang="ts">
import { computed, inject, ref, unref, watch } from 'vue';
import debugCode from '../debugCode.vue';
import { debounce } from 'lodash';
import { computeDynamicValue } from './computeDynamicValue';

defineOptions({
  name: 'DynamicValueField',
});

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
  value: {
    type: String,
    default: () => undefined,
  },
  innerModel: {
    type: Object,
    default: null,
  },
  globalInnerModel: {
    default: null,
  },
  formModel: {
    type: Object,
    default: null,
  },
  context: {
    type: Object,
    default: null,
  },
  baseKeyPath: {
    type: Array,
    default: () => [],
  },
  workerHostFunctions: {
    type: Object,
    default: () => {},
  },
  is_form_design: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:value']);

// 兼容 FormRender3 的 provide/inject：结构锁定（修改模式）下禁止 M5 覆盖ID 自动分配
const lockStructureInject = inject('FORM_LOCK_STRUCTURE', ref(false));
const isLocked = computed(() => !!unref(lockStructureInject));

const errorMsg = ref<string | null>(null);
// 编辑/设计模式下的内部展示值（不 emit 到外部，仅用于展示）
const displayValue = ref<string>(props.value || '');
const _value = computed(() => {
  const currentValue = props.is_form_design ? displayValue.value : props.value;
  return currentValue;
});

const syncValue = debounce(
  async (innerModel: any, globalInnerModel: any, field: any, formModel: any) => {
    const result = await computeDynamicValue({
      field,
      currentValue: _value.value,
      innerModel,
      globalInnerModel,
      formModel,
      context: props.context,
      baseKeyPath: props.baseKeyPath,
      workerHostFunctions: props.workerHostFunctions || {},
      is_form_design: props.is_form_design,
      lock_structure: isLocked.value,
    });

    if (result) {
      if (props.is_form_design) {
        // 编辑/设计模式：只更新内部展示值，不 emit 到外部（无回填循环风险，完整展示计算结果）
        displayValue.value = result.value ?? '';
      } else {
        // 空值不回填（避免触发 watch 造成无限循环），错误信息展示
        if (result.value !== undefined && result.value !== null) {
          emit('update:value', result.value);
        }
      }
      if (field.watchType === 'code' || field.watchType === 'serial') {
        errorMsg.value = result.error || null;
      }
    }
  },
  200,
);

watch(
  () => [props.innerModel, props.globalInnerModel, props.field, props.formModel, props.context],
  ([innerModel, globalInnerModel, field, formModel]) => {
    syncValue(innerModel, globalInnerModel, field, formModel);
  },
  {
    immediate: true,
    deep: true,
  },
);
</script>

<style lang="less" scoped>
.error-msg {
  color: #ff4d4f;
}
</style>
